import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import './AttendanceImportModal.css';
import {
  FiAlertCircle,
  FiArrowRight,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiDownload,
  FiFileText,
  FiPlus,
  FiRefreshCw,
  FiUpload,
  FiX,
  FiXCircle,
  FiSearch,
} from "react-icons/fi";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import AttendanceStats from "./components/AttendanceStats";
import ManualAttendanceModal from "./components/ManualAttendanceModal";
import HrmsFilters from '../../components/hrms/HrmsFilterPanel';
import { matchesHrmsFilters } from '../../components/hrms/hrmsFilterUtils';

const statusConfig = {
  PRESENT: ["Present", "bg-green-50 text-green-700", FiCheckCircle],
  LATE: ["Late", "bg-orange-50 text-orange-700", FiClock],
  ABSENT: ["Absent", "bg-red-50 text-red-700", FiXCircle],
  ON_LEAVE: ["On Leave", "bg-blue-50 text-blue-700", FiCalendar],
  HALF_DAY: ["Half Day", "bg-purple-50 text-purple-700", FiClock],
  WEEKLY_OFF: ["Weekly Off", "bg-gray-100 text-gray-700", FiCalendar],
  HOLIDAY: ["Holiday", "bg-indigo-50 text-indigo-700", FiCalendar],
};

const indiaToday = () =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
  }).format(new Date());

const formatTime = (value) =>
  value
    ? new Date(value).toLocaleTimeString("en-IN", {
        timeZone: "Asia/Kolkata",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    : "—";

const formatMinutes = (value) => {
  if (value === null || value === undefined) return "—";

  const minutes = Number(value || 0);
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (!hours) return `${remainingMinutes} min`;
  if (!remainingMinutes) return `${hours}h`;

  return `${hours}h ${remainingMinutes}m`;
};

function SessionTimeline({ employee }) {
  const sessions = employee.sessions || [];

  if (!sessions.length) {
    return <span className="text-sm text-gray-400">No work sessions</span>;
  }

  return (
    <div className="flex max-w-full flex-wrap gap-2">
      {sessions.map((session, index) => (
        <span
          key={session.id || index}
          className="rounded-lg bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700"
        >
          {formatTime(session.checkInAt)} – {formatTime(session.checkOutAt)}
        </span>
      ))}
    </div>
  );
}

function attendanceImportRows(section, employee) {
  const joiningDate = employee?.joiningDate?.slice(0, 10);
  return (section?.rows || []).map((row) => ({
    ...row,
    skippedBeforeJoining: Boolean(joiningDate && row.attendanceDate < joiningDate),
  }));
}

export function ImportAttendanceModal({ open, onClose, accessToken, onImported }) {
  const dialogRef=useRef(null),closeRef=useRef(null);
  const [matchSearch,setMatchSearch]=useState(''),[matchFilter,setMatchFilter]=useState('ALL'),[matchPage,setMatchPage]=useState(0),[importScope,setImportScope]=useState('SELECTED');
  const [locationMachines,setLocationMachines]=useState({});
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [selectedSectionIndex, setSelectedSectionIndex] = useState("");
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState("");
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [sectionMappings, setSectionMappings] = useState({});

  const selectedSection = useMemo(() => {
    if (!preview?.sections?.length) return null;

    return (
      preview.sections.find(
        (section) =>
          Number(section.sectionIndex) === Number(selectedSectionIndex),
      ) || preview.sections[0]
    );
  }, [preview, selectedSectionIndex]);

  const reset = () => {
    setMatchSearch('');setMatchFilter('ALL');setMatchPage(0);setImportScope('SELECTED');
    setFile(null);
    setLocationMachines({});
    setPreview(null);
    setSelectedSectionIndex("");
    setEmployees([]);
    setEmployeeId("");
    setReplaceExisting(false);
    setError("");
    setSuccess("");
    setSectionMappings({});
  };

  const close = () => {
    if (isPreviewing || isImporting) return;

    reset();
    onClose();
  };
  closeRef.current=close;
  useEffect(()=>{
    if(!open)return;
    const previous=document.activeElement,overflow=document.body.style.overflow;document.body.style.overflow='hidden';
    dialogRef.current?.focus();
    const keys=event=>{if(event.key==='Escape'){event.preventDefault();closeRef.current?.();}if(event.key==='Tab'){const elements=Array.from(dialogRef.current?.querySelectorAll('button:not(:disabled),input:not(:disabled):not([type=hidden]),select:not(:disabled),[tabindex="0"]')||[]).filter(el=>el.getClientRects().length);if(!elements.length){event.preventDefault();return;}const first=elements[0],last=elements.at(-1);if(event.shiftKey&&(document.activeElement===first||document.activeElement===dialogRef.current)){event.preventDefault();last.focus();}else if(!event.shiftKey&&(document.activeElement===last||document.activeElement===dialogRef.current)){event.preventDefault();first.focus();}}};
    document.addEventListener('keydown',keys);return()=>{document.removeEventListener('keydown',keys);document.body.style.overflow=overflow;previous?.focus?.();};
  },[open]);

  const loadEmployeeMatches = async (source) => {
    const response = await apiRequest("/attendance/admin/employees", {
      accessToken,
    });

    const records = Array.isArray(response)
      ? response
      : response?.employees || response?.items || response?.data || [];

    const sourceCode = String(source?.employeeCode || "")
      .trim()
      .toUpperCase();

    const sourceName = String(source?.employeeName || "")
      .trim()
      .replace(/\s+/g, " ")
      .toUpperCase();

    return records
      .map((employee) => {
        const id = employee.id || employee.employeeId;

        if (!id) return null;

        return {
          id,
          employeeCode: employee.employeeCode || "",
          fullName: employee.fullName || "Unnamed employee",
          joiningDate: employee.joiningDate || null,
          designation:
            employee.designation ||
            employee.assignments?.[0]?.designation?.name ||
            null,
          isSuggested:
            (sourceCode &&
              String(employee.employeeCode || "")
                .trim()
                .toUpperCase() === sourceCode) ||
            (sourceName &&
              String(employee.fullName || "")
                .trim()
                .replace(/\s+/g, " ")
                .toUpperCase() === sourceName),
        };
      })
      .filter(Boolean)
      .sort((first, second) => {
        if (first.isSuggested !== second.isSuggested) {
          return first.isSuggested ? -1 : 1;
        }

        return first.fullName.localeCompare(second.fullName);
      });
  };

  const chooseSection = async (sectionIndex, response = preview) => {
    const section = response?.sections?.find(
      (item) => Number(item.sectionIndex) === Number(sectionIndex),
    );

    if (!section) return;

    setSelectedSectionIndex(String(section.sectionIndex));
    setEmployees([]);
    setEmployeeId("");
    setError("");
    setSuccess("");

    try {
      const matchedEmployees = await loadEmployeeMatches(section.source);

      if (
        section.suggestedEmployee &&
        !matchedEmployees.some(
          (employee) => employee.id === section.suggestedEmployee.id,
        )
      ) {
        matchedEmployees.unshift(section.suggestedEmployee);
      }

      setEmployees(matchedEmployees);
      setEmployeeId(
        ['MACHINE_MATRIX','ESSL_TIME_REGISTER'].includes(response?.format)?(section.suggestedEmployee?.id||""):(sectionMappings[String(section.sectionIndex)] || section.suggestedEmployee?.id || ""),
      );
    } catch (requestError) {
      setError(
        requestError.message ||
          "Employee matches could not be loaded. Select an employee manually.",
      );
    }
  };

  const previewFile = async (selectedFile) => {
    if (!selectedFile) return;

    setFile(selectedFile);
    setPreview(null);
    setSelectedSectionIndex("");
    setEmployees([]);
    setEmployeeId("");
    setError("");
    setSuccess("");
    setIsPreviewing(true);
    setMatchPage(0);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("locationMachines",JSON.stringify(locationMachines));

      const response = await apiRequest("/attendance/imports/excel/preview", {
        method: "POST",
        body: formData,
        accessToken,
      });

      if (!response.sections?.length) {
        throw new Error(
          "No employee attendance sections were found in this file.",
        );
      }

      setPreview(response);
      setSectionMappings(Object.fromEntries(response.sections.map((section) => [String(section.sectionIndex), section.suggestedEmployee?.id || ""])));

      await chooseSection(response.sections[0].sectionIndex, response);
    } catch (requestError) {
      setError(
        requestError.message || "Unable to read this attendance report.",
      );
    } finally {
      setIsPreviewing(false);
    }
  };

  const machineSelectionsReviewed = !['MACHINE_MATRIX','ESSL_TIME_REGISTER'].includes(preview?.format) || preview.locations.every(location=>locationMachines[location] && locationMachines[location]===preview.locationMachines?.[location]);

  const confirmImport = async () => {
    if(!machineSelectionsReviewed){setError('Refresh and review machine matches before importing.');return;}
    if (!file || !preview || !selectedSection) {
      setError("Upload and preview an attendance report first.");
      return;
    }

    if (!employeeId) {
      setError("Select the employee whose attendance should be imported.");
      return;
    }

    if (attendanceImportRows(selectedSection, employees.find((employee) => employee.id === employeeId))
      .some((row) => !row.skippedBeforeJoining && row.errors?.length)) {
      setError("Fix the invalid rows shown below before importing.");
      return;
    }

    setError("");
    setSuccess("");
    setIsImporting(true);

    try {
      const formData = new FormData();

      formData.append("file", file);
      formData.append("locationMachines",JSON.stringify(locationMachines));
      formData.append("employeeId", employeeId);
      formData.append("sectionIndex", String(selectedSection.sectionIndex));
      formData.append("replaceExisting", String(replaceExisting));

      const result = await apiRequest("/attendance/imports/excel/confirm", {
        method: "POST",
        body: formData,
        accessToken,
      });

      setSuccess(
        `${result.imported} attendance day(s) imported successfully${
          result.skippedExisting
            ? ` · ${result.skippedExisting} existing day(s) skipped`
            : ""
        }${result.skippedBeforeJoining ? ` · ${result.skippedBeforeJoining} day(s) before joining skipped` : ""}.`,
      );

      await onImported();
    } catch (requestError) {
      setError(requestError.message || "Unable to import attendance.");
    } finally {
      setIsImporting(false);
    }
  };

  const confirmBulkImport = async () => {
    if(!machineSelectionsReviewed){setError('Refresh and review machine matches before importing.');return;}
    if (!file || !preview?.sections?.length) {
      setError("Upload and preview an attendance report first.");
      return;
    }

    if (preview.sections.some((section) => {
      const id = sectionMappings[String(section.sectionIndex)];
      if (!id) return false;
      const employee = employees.find((item) => item.id === id) ||
        (section.suggestedEmployee?.id === id ? section.suggestedEmployee : null);
      return attendanceImportRows(section, employee).some((row) => !row.skippedBeforeJoining && row.errors?.length);
    })) {
      setError("Fix the invalid rows in every employee section before bulk import.");
      return;
    }
    if (!preview.sections.some((section) => sectionMappings[String(section.sectionIndex)])) {
      setError("No matched employees to import.");
      return;
    }
    if (!window.confirm(`Import ${matchedCount} matched employee section(s)? ${preview.sections.length-matchedCount} unmatched section(s) will be skipped.`)) return;

    setError("");
    setSuccess("");
    setIsImporting(true);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("locationMachines",JSON.stringify(locationMachines));
      formData.append("replaceExisting", String(replaceExisting));
      formData.append("mappings", JSON.stringify(sectionMappings));

      const result = await apiRequest("/attendance/imports/excel/confirm-all", {
        method: "POST",
        body: formData,
        accessToken,
      });

      setSuccess(
        String(result.imported) +
          " attendance day(s) imported for " +
          String(result.employeeCount) +
          " employee(s)" +
          (result.skippedExisting
            ? " · " + String(result.skippedExisting) + " existing day(s) skipped"
            : "") +
          (result.skippedBeforeJoining
            ? ` · ${result.skippedBeforeJoining} day(s) before joining skipped`
            : "") +
          (result.skippedUnmatchedEmployees ? ` · ${result.skippedUnmatchedEmployees} unmatched employee section(s) skipped (${result.skippedUnmatchedRows} days)` : "") +
          (result.skippedEmployees ? ` · ${result.skippedEmployees} employee(s) had no eligible dates` : "") +
          ".",
      );

      await onImported();
    } catch (requestError) {
      setError(requestError.message || "Unable to bulk import attendance.");
    } finally {
      setIsImporting(false);
    }
  };

  if (!open) return null;

  const selectedEmployee = employees.find((employee) => employee.id === employeeId);
  const rows = attendanceImportRows(selectedSection, selectedEmployee);
  const invalidRows = rows.filter((row) => !row.skippedBeforeJoining && row.errors?.length);
  const skippedBeforeJoining = rows.filter((row) => row.skippedBeforeJoining).length;
  const readyRows = rows.length - skippedBeforeJoining - invalidRows.length;
  const matchedCount=(preview?.sections||[]).filter(s=>sectionMappings[String(s.sectionIndex)]).length;
  const filteredSections=(preview?.sections||[]).filter(s=>{const matched=!!sectionMappings[String(s.sectionIndex)];return (matchFilter==='ALL'||(matchFilter==='MATCHED'?matched:!matched))&&`${s.source?.employeeName||''} ${s.source?.employeeCode||''} ${s.source?.location||''} ${s.suggestedEmployee?.fullName||''} ${s.suggestedEmployee?.employeeCode||''}`.toLowerCase().includes(matchSearch.toLowerCase());});
  const matchingPages=Math.max(1,Math.ceil(filteredSections.length/10));
  const visibleMatchPage=Math.min(matchPage,matchingPages-1);
  const allReady=!!preview?.sections?.length&&matchedCount>0&&machineSelectionsReviewed&&!preview.sections.filter(s=>sectionMappings[String(s.sectionIndex)]).some(s=>attendanceImportRows(s,employees.find(e=>e.id===sectionMappings[String(s.sectionIndex)])||s.suggestedEmployee).some(r=>!r.skippedBeforeJoining&&r.errors?.length));

  return (
    <div className="attendance-import-overlay">
      <div className="attendance-import-position">
        <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="attendance-import-title" tabIndex={-1} className="attendance-import-dialog">
          <div className="attendance-import-header">
            <div>
              <p className="text-sm text-gray-500">Attendance management</p>
              <h2 id="attendance-import-title" className="mt-1 text-xl font-bold text-gray-900">
                Import Attendance Report
              </h2>
              <p className="mt-2 text-sm text-gray-500">
                Upload an eSSL Time Register or monthly Attendance Register.
                Review employee sections and machine matches before importing.
              </p>
            </div>

            <button
              type="button"
              onClick={close}
              aria-label="Close attendance import"
              disabled={isPreviewing || isImporting}
              className="flex h-10 w-10 items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100 disabled:opacity-50"
            >
              <FiX size={22} />
            </button>
          </div>

          <ol className="attendance-import-steps" aria-label="Import progress">{[['Upload workbook',!preview],['Match employees',!!preview&&(!machineSelectionsReviewed||matchedCount!==preview.sections.length)],['Review & import',!!preview&&machineSelectionsReviewed&&matchedCount===preview.sections.length]].map(([label,active],i)=><li key={label} aria-current={active?'step':undefined}><span>{i+1}</span><strong>{label}</strong></li>)}</ol>
          <div className="attendance-import-body space-y-5">
            {!preview ? (
              <label className="attendance-import-upload">
                <FiUpload className="text-primary-800" size={34} />

                <p className="mt-4 text-lg font-bold text-gray-900">
                  Select attendance Excel
                </p>

                <p className="mt-2 max-w-md text-sm text-gray-500">
                  Use an original eSSL Time Register or monthly Attendance Register (.xlsx or .xls).
                  Multiple employees are supported. Monthly registers let you choose a face machine for each location.
                </p>

                <span className="mt-5 rounded-xl bg-primary-800 px-5 py-2.5 text-sm font-semibold text-white">
                  Choose Excel File
                </span>

                <input
                  type="file"
                  accept=".xlsx,.xls"
                  className="sr-only"
                  aria-label="Choose attendance Excel file"
                  disabled={isPreviewing}
                  onChange={(event) => previewFile(event.target.files?.[0])}
                />
              </label>
            ) : (
              <>
                {['MACHINE_MATRIX','ESSL_TIME_REGISTER'].includes(preview.format)&&<section className="rounded-2xl border border-primary-100 bg-primary-50 p-5"><h3 className="font-semibold text-gray-900">Match workbook locations to face machines</h3><p className="mt-1 text-sm text-gray-600">{preview.format==='ESSL_TIME_REGISTER'?'Excel Biometric Code is the machine-user ID. Employee Code and Employee Name are reference only.':'Excel Employee Code is the machine-user ID in this matrix layout.'} Select each location’s machine, then refresh the preview. Employees are resolved using dated mappings only.</p><div className="mt-4 grid gap-4 sm:grid-cols-2">{preview.locations.map(location=><label key={location} className="text-sm font-semibold">{location}<select aria-label={`Machine for ${location}`} className="mt-2 block w-full rounded-xl border bg-white p-3" value={locationMachines[location]||''} onChange={e=>{setLocationMachines(current=>({...current,[location]:e.target.value}));setSectionMappings({});setEmployeeId('');}}><option value="">Choose face machine</option>{preview.machines.map(machine=><option key={machine.id} value={machine.id}>{machine.name} · {machine.code}</option>)}</select></label>)}</div><button type="button" disabled={isPreviewing||isImporting} onClick={()=>previewFile(file)} className="mt-4 rounded-xl bg-primary-800 px-4 py-2 text-white">Refresh machine matches</button><p className="mt-3 text-xs text-gray-500">{preview.identifierNote}</p><p className="mt-3 text-xs text-gray-500">Bulk import skips unmatched and ambiguous IDs and imports verified matches only. Existing attendance is skipped unless you explicitly select replacement.</p></section>}
                <div className="grid gap-4 lg:grid-cols-[1fr_1fr_1fr_auto]">
                  <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                    <div className="flex items-center gap-3">
                      <FiFileText className="text-primary-800" size={24} />

                      <div className="min-w-0">
                        <p className="truncate font-semibold text-gray-900">
                          {file?.name}
                        </p>
                        <p className="text-sm text-gray-500">
                          {preview.sectionCount ||
                            preview.sections?.length ||
                            0}{" "}
                          employee section(s) found
                        </p>
                      </div>
                    </div>
                  </div>

                  <label>
                    <span className="mb-2 block text-sm font-semibold text-gray-700">
                      Employee section in Excel
                    </span>

                    <select
                      value={selectedSectionIndex}
                      disabled={isImporting}
                      onChange={(event) => chooseSection(event.target.value)}
                      className="h-12 w-full rounded-xl border border-gray-200 bg-white px-4 outline-none focus:border-primary-700 focus:ring-2 focus:ring-primary-100"
                    >
                      {preview.sections.map((section) => (
                        <option
                          key={section.sectionIndex}
                          value={section.sectionIndex}
                        >
                          {section.source?.employeeName || "Unknown employee"} ·{" "}
                          {section.source?.employeeCode || "No code"} ·{" "}
                          {section.summary?.totalRows || 0} days
                        </option>
                      ))}
                    </select>
                  </label>

                  <label>
                    <span className="mb-2 block text-sm font-semibold text-gray-700">
                      Import attendance for
                    </span>

                    <select
                      value={employeeId}
                      disabled={isImporting||['MACHINE_MATRIX','ESSL_TIME_REGISTER'].includes(preview.format)}
                      onChange={(event) => {
                        setEmployeeId(event.target.value);
                        setSectionMappings((current) => ({ ...current, [String(selectedSectionIndex)]: event.target.value }));
                      }}
                      className="h-12 w-full rounded-xl border border-gray-200 bg-white px-4 outline-none focus:border-primary-700 focus:ring-2 focus:ring-primary-100"
                    >
                      <option value="">Select employee</option>

                      {employees.map((employee) => (
                        <option key={employee.id} value={employee.id}>
                          {employee.fullName} · {employee.employeeCode}
                          {employee.designation
                            ? ` · ${employee.designation}`
                            : ""}
                        </option>
                      ))}
                    </select>
                  </label>

                  <button
                    type="button"
                    onClick={reset}
                    disabled={isImporting}
                    className="mt-auto inline-flex h-12 items-center justify-center gap-2 rounded-xl border border-gray-200 px-4 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <FiRefreshCw />
                    Change file
                  </button>
                </div>

                <div className="rounded-2xl border border-primary-100 bg-primary-50 p-4 text-sm text-primary-950">
                  <b>Selected source:</b>{" "}
                  {selectedSection?.source?.employeeName || "Employee unknown"}{" "}
                  ·{" "}
                  {selectedSection?.source?.employeeCode || "No employee code"}
                  {selectedSection?.source?.biometricCode
                    ? ` · Biometric ID ${selectedSection.source.biometricCode}`
                    : ""}
                </div>

                <section className="attendance-import-matches"><header><div><h3>Review employee matches</h3><p>{matchedCount} matched · {(preview.sections.length-matchedCount)} need attention · {preview.sections.length} total</p></div><div className="attendance-import-match-tools"><input type="search" aria-label="Search employee matches" placeholder="Search employee, ID or location" value={matchSearch} onChange={e=>{setMatchSearch(e.target.value);setMatchPage(0);}}/><select aria-label="Employee match status" value={matchFilter} onChange={e=>{setMatchFilter(e.target.value);setMatchPage(0);}}><option value="ALL">All matches</option><option value="UNMATCHED">Needs matching</option><option value="MATCHED">Matched</option></select></div></header><div className="max-h-80 overflow-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-primary-800 text-white text-left"><tr><th className="p-3">Excel employee</th><th className="p-3">Match status</th><th className="p-3">ERP employee — review or select manually</th></tr></thead>
                    <tbody>{filteredSections.slice(visibleMatchPage*10,(visibleMatchPage+1)*10).map((section) => (
                      <tr key={section.sectionIndex} className="border-t border-gray-200">
                        <td className="p-3"><button type="button" className="font-semibold text-primary-800 hover:underline" onClick={()=>chooseSection(section.sectionIndex)}>{section.source?.employeeName || "Unknown"}</button><br />{section.source?.employeeCode}{section.source?.location&&<span className="block text-xs text-gray-500">{section.source.location}</span>}</td>
                        <td className="p-3"><span className={`attendance-match-badge ${sectionMappings[String(section.sectionIndex)]?'is-matched':'needs-match'}`}>{sectionMappings[String(section.sectionIndex)] ? "Matched" : "Needs matching"}</span>{!sectionMappings[String(section.sectionIndex)]&&section.mappingIssue&&<p className="mt-2 max-w-xs text-xs text-gray-500">{section.mappingIssue}</p>}</td>
                        <td className="p-3"><select className="w-full border rounded-xl p-2" disabled={isImporting||['MACHINE_MATRIX','ESSL_TIME_REGISTER'].includes(preview.format)} value={sectionMappings[String(section.sectionIndex)] || ""} onChange={(event) => {
                          const id = event.target.value;
                          setSectionMappings((current) => ({ ...current, [String(section.sectionIndex)]: id }));
                          if (String(section.sectionIndex) === selectedSectionIndex) setEmployeeId(id);
                        }}><option value="">Select correct employee</option>{employees.map((employee) => <option key={employee.id} value={employee.id}>{employee.fullName} · {employee.employeeCode}</option>)}</select></td>
                      </tr>
                    ))}{!filteredSections.length&&<tr><td colSpan={3} className="p-8 text-center text-gray-500">No employee matches found.</td></tr>}</tbody>
                  </table></div><footer><span>{filteredSections.length} employees · 10 per page</span><div><button type="button" disabled={!visibleMatchPage} onClick={()=>setMatchPage(Math.max(0,visibleMatchPage-1))}>Previous</button><span>{visibleMatchPage+1} / {matchingPages}</span><button type="button" disabled={visibleMatchPage+1>=matchingPages} onClick={()=>setMatchPage(visibleMatchPage+1)}>Next</button></div></footer></section>
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
                  {[
                    [
                      "Source rows",
                      selectedSection?.summary?.totalRows || 0,
                      "text-gray-900",
                    ],
                    [
                      "Valid dates",
                      readyRows,
                      "text-green-700",
                    ],
                    [
                      "Needs attention",
                      invalidRows.length,
                      "text-red-700",
                    ],
                    [
                      "Before joining — skipped",
                      skippedBeforeJoining,
                      "text-amber-700",
                    ],
                    [
                      "Period",
                      `${selectedSection?.summary?.firstDate || "—"} → ${
                        selectedSection?.summary?.lastDate || "—"
                      }`,
                      "text-primary-800",
                    ],
                  ].map(([label, value, color]) => (
                    <div
                      key={label}
                      className="rounded-2xl border border-gray-100 bg-white p-4"
                    >
                      <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                        {label}
                      </p>
                      <p className={`mt-2 text-lg font-bold ${color}`}>
                        {value}
                      </p>
                    </div>
                  ))}
                </div>

                <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4">
                  <input
                    type="checkbox"
                    checked={replaceExisting}
                    disabled={isImporting}
                    onChange={(event) =>
                      setReplaceExisting(event.target.checked)
                    }
                    className="mt-1 h-4 w-4 accent-primary-800"
                  />

                  <span>
                    <span className="block font-semibold text-amber-900">
                      Replace existing attendance for imported dates
                    </span>

                    <span className="mt-1 block text-sm text-amber-800">
                      Keep this unchecked to skip existing dates safely. Enable
                      it only when correcting already-recorded attendance.
                    </span>
                  </span>
                </label>

                <div className="overflow-hidden rounded-2xl border border-gray-100">
                  <div className="border-b bg-gray-50 px-5 py-4">
                    <h3 className="font-bold text-gray-900">Import preview</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      The selected employee section is shown below. First IN and
                      final OUT become one work session for each day.
                    </p>
                  </div>

                  <div className="max-h-[340px] overflow-auto">
                    <table className="w-full min-w-[780px] text-sm">
                      <thead className="sticky top-0 bg-white text-left text-xs uppercase tracking-wider text-gray-500 shadow-sm">
                        <tr>
                          <th className="px-5 py-3">Date</th>
                          <th className="px-5 py-3">Sheet status</th>
                          <th className="px-5 py-3">ERP status</th>
                          <th className="px-5 py-3">IN</th>
                          <th className="px-5 py-3">OUT</th>
                          <th className="px-5 py-3">Validation</th>
                        </tr>
                      </thead>

                      <tbody>
                        {rows.map((row) => (
                          <tr
                            key={`${row.attendanceDate}-${row.rowNumber}`}
                            className="border-t border-gray-100"
                          >
                            <td className="px-5 py-3 font-medium text-gray-900">
                              {row.attendanceDate}
                            </td>

                            <td className="px-5 py-3">
                              {row.sourceStatus || "—"}
                            </td>

                            <td className="px-5 py-3">
                              <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700">
                                {row.skippedBeforeJoining ? "NOT IMPORTED" : row.status?.replaceAll("_", " ") || "INVALID"}
                              </span>
                            </td>

                            <td className="px-5 py-3">
                              {row.checkInTime || "—"}
                            </td>

                            <td className="px-5 py-3">
                              {row.checkOutTime || "—"}
                            </td>

                            <td className="px-5 py-3">
                              {row.skippedBeforeJoining ? (
                                <span className="text-xs font-semibold text-amber-700">
                                  Skipped — before joining ({selectedEmployee?.joiningDate?.slice(0, 10)})
                                </span>
                              ) : row.errors?.length ? (
                                <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700">
                                  <FiAlertCircle />
                                  {row.errors.join(" · ")}
                                </span>
                              ) : row.warnings?.length ? (
                                <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700">
                                  <FiAlertCircle />
                                  {row.warnings.join(" · ")}
                                </span>
                              ) : (
                                <span className="text-xs font-semibold text-green-700">
                                  Ready
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}

                        {!rows.length && (
                          <tr>
                            <td
                              colSpan={6}
                              className="px-5 py-12 text-center text-gray-500"
                            >
                              No attendance rows found in this employee section.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {isPreviewing && (
              <div className="flex items-center justify-center gap-3 rounded-2xl bg-gray-50 p-6 text-sm font-medium text-gray-600">
                <span className="h-5 w-5 animate-spin rounded-full border-2 border-primary-100 border-t-primary-800" />
                Reading attendance workbook...
              </div>
            )}

            {error && (
              <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            {success && (
              <div role="status" className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                {success}
              </div>
            )}
          </div>

          <div className="attendance-import-footer"><div className="attendance-import-scope">{preview?<><label htmlFor="attendance-import-scope">Import scope</label><select id="attendance-import-scope" value={importScope} disabled={isImporting||isPreviewing} onChange={e=>setImportScope(e.target.value)}><option value="SELECTED">Selected employee</option><option value="ALL">Complete workbook · {preview.sections.length} employees</option></select><small>{replaceExisting?'Replacement enabled · existing attendance may change':'Existing attendance will be skipped'}</small></>:<p>Excel files only · Maximum 20 MB<br/><span>No records change until you confirm an import.</span></p>}</div><div className="attendance-import-actions">
            <button
              type="button"
              onClick={close}
              disabled={isPreviewing || isImporting}
              className="h-11 rounded-xl border border-gray-200 px-5 font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              type="button"
              disabled={
                !preview ||
                !selectedSection ||
                (importScope==='ALL'?!allReady:(!employeeId||Boolean(invalidRows.length)||!machineSelectionsReviewed)) ||
                isImporting || isPreviewing
              }
              onClick={importScope==='ALL'?confirmBulkImport:confirmImport}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-primary-800 px-5 font-semibold text-white hover:bg-primary-900 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FiUpload />
              {isImporting ? "Importing..." : importScope==='ALL'?"Import all matched":"Confirm Import"}
            </button>
          </div></div>
        </div>
      </div>
    </div>
  );
}

export default function Attendance() {
  const { accessToken } = useAuth();

  const ITEMS_PER_PAGE = 10;

  const [selectedDate, setSelectedDate] = useState(indiaToday);
  const [dailyData, setDailyData] = useState({
    summary: { present: 0, absent: 0, onLeave: 0, late: 0 },
    employees: [],
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [manualOpen, setManualOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [debouncedEmployeeSearch, setDebouncedEmployeeSearch] = useState("");
  const [multipleSessionsOnly, setMultipleSessionsOnly] = useState(false);
  const [hrmsFilters, setHrmsFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedEmployeeSearch(employeeSearch.trim());
      setCurrentPage(1);
    }, 350);

    return () => window.clearTimeout(timeout);
  }, [employeeSearch]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedDate, multipleSessionsOnly]);

  const loadAttendance = useCallback(async () => {
    if (!accessToken) return;

    try {
      setLoading(true);
      setError("");

      const params = new URLSearchParams({
        date: selectedDate,
      });

      if (debouncedEmployeeSearch) {
        params.set("search", debouncedEmployeeSearch);
      }
      if (multipleSessionsOnly) {
        params.set("multipleSessionsOnly", "true");
      }

      const response = await apiRequest(
        `/attendance/admin/daily?${params.toString()}`,
        { accessToken },
      );

      setDailyData(response);
    } catch (requestError) {
      setError(requestError.message || "Unable to load daily attendance.");
    } finally {
      setLoading(false);
    }
  }, [
    accessToken,
    selectedDate,
    debouncedEmployeeSearch,
    multipleSessionsOnly,
  ]);

  useEffect(() => {
    loadAttendance();
  }, [loadAttendance]);

  const employees = useMemo(() => (dailyData.employees || []).filter(row => matchesHrmsFilters(row, hrmsFilters)), [dailyData.employees, hrmsFilters]);
  const filteredSummary = useMemo(() => ({
    present: employees.filter(row => ['PRESENT','LATE'].includes(row.status)).length,
    absent: employees.filter(row => row.status === 'ABSENT').length,
    onLeave: employees.filter(row => row.status === 'ON_LEAVE').length,
    late: employees.filter(row => row.status === 'LATE').length,
    multipleSessionEmployees: employees.filter(row => (row.sessions || []).length > 1).length,
    multipleSessionCount: employees.reduce((sum,row) => sum + ((row.sessions || []).length > 1 ? row.sessions.length : 0),0),
  }), [employees]);

  const totalWorked = useMemo(
    () =>
      employees.reduce(
        (total, employee) => total + Number(employee.workingMinutes || 0),
        0,
      ),
    [employees],
  );

  const totalPages = Math.max(1, Math.ceil(employees.length / ITEMS_PER_PAGE));

  const visibleEmployees = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return employees.slice(start, start + ITEMS_PER_PAGE);
  }, [employees, currentPage]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const pageNumbers = useMemo(() => {
    const pages = [];

    const start = Math.max(1, currentPage - 2);
    const end = Math.min(totalPages, currentPage + 2);

    for (let page = start; page <= end; page += 1) {
      pages.push(page);
    }

    return pages;
  }, [currentPage, totalPages]);

  const startItem =
    employees.length === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1;

  const endItem = Math.min(currentPage * ITEMS_PER_PAGE, employees.length);

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Attendance</h1>
          <p className="mt-2 text-gray-500">
            Track work sessions, import eSSL reports, and manage daily
            attendance.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <input
            type="date"
            value={selectedDate}
            onChange={(event) => setSelectedDate(event.target.value)}
            className="rounded-xl border border-gray-200 px-4 py-3 outline-none focus:border-red-800 focus:ring-2 focus:ring-red-100"
          />

          <button
            type="button"
            onClick={() => setImportOpen(true)}
            className="inline-flex items-center gap-2 rounded-xl border border-primary-200 bg-primary-50 px-5 py-3 font-medium text-primary-800 hover:bg-primary-100"
          >
            <FiDownload />
            Import Attendance
          </button>

          <button
            type="button"
            onClick={() => setManualOpen(true)}
            className="inline-flex items-center gap-2 rounded-xl bg-red-800 px-5 py-3 font-medium text-white hover:bg-red-900"
          >
            <FiPlus />
            Mark Attendance
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-6">
        <AttendanceStats title="Present" count={filteredSummary.present} />
        <AttendanceStats title="Absent" count={filteredSummary.absent} />
        <AttendanceStats title="On Leave" count={filteredSummary.onLeave} />
        <AttendanceStats title="Late" count={filteredSummary.late} />
        <AttendanceStats
          title="Multiple sessions"
          count={filteredSummary.multipleSessionEmployees}
          caption={`${filteredSummary.multipleSessionCount} work sessions across flagged employees`}
        />
        <AttendanceStats
          title="Worked time"
          count={formatMinutes(totalWorked)}
          caption="Sum of completed sessions"
        />
      </div>

      <div className="my-5"><HrmsFilters records={dailyData.employees || []} value={hrmsFilters} onChange={next => { setHrmsFilters(next); setCurrentPage(1); }} fields={['branch','department','designation','employmentType','employeeStatus','recordStatus']} /></div>
      <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm">
        <div className="flex flex-col gap-4 border-b p-6 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Daily attendance snapshot
            </h2>

            <p className="mt-1 text-sm text-gray-500">
              {selectedDate} · Session gaps are excluded from worked time.
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative w-full sm:w-80">
              <FiSearch
                size={18}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
              />

              <input
                type="text"
                value={employeeSearch}
                onChange={(event) => setEmployeeSearch(event.target.value)}
                placeholder="Search employee, nickname or ID..."
                className="w-full rounded-xl border border-gray-200 py-3 pl-11 pr-4 text-sm outline-none transition focus:border-red-800 focus:ring-2 focus:ring-red-100"
              />
            </div>

            <label className="flex h-11 shrink-0 items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 text-sm font-semibold text-amber-900">
              <input
                type="checkbox"
                checked={multipleSessionsOnly}
                onChange={(event) =>
                  setMultipleSessionsOnly(event.target.checked)
                }
                className="h-4 w-4 rounded border-amber-300 text-primary-700 focus:ring-primary-600"
              />
              Multiple sessions only
            </label>

            <FiClock
              className="hidden rounded-xl bg-red-800 p-3 text-white sm:block"
              size={46}
            />
          </div>
        </div>

        <div className="divide-y">
          {loading ? (
            <div className="p-16 text-center text-gray-500">
              Loading attendance…
            </div>
          ) : employees.length === 0 ? (
            <div className="p-16 text-center text-gray-500">
              {employeeSearch
                ? "No employees match your search."
                : "No active employees found."}
            </div>
          ) : (
            visibleEmployees.map((employee) => {
              const [label, classes, Icon] =
                statusConfig[employee.status] || statusConfig.ABSENT;

              const displayName = employee.nickname || employee.fullName;
              const sessionCount = employee.sessions?.length || 0;
              const hasMultipleSessions = sessionCount > 1;

              return (
                <div
                  key={employee.employeeId}
                  className={`grid gap-4 border-l-4 p-5 transition hover:bg-gray-50 lg:grid-cols-[minmax(220px,1fr)_minmax(330px,2fr)_auto] ${
                    hasMultipleSessions
                      ? "border-amber-400 bg-amber-50/50"
                      : "border-transparent"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-red-800 font-semibold text-white">
                      {employee.fullName
                        .split(" ")
                        .map((word) => word[0])
                        .join("")
                        .slice(0, 2)}
                    </div>

                    <div className="min-w-0">
                      <b className="block truncate text-gray-900">
                        {displayName}
                      </b>

                      {employee.nickname && (
                        <p className="truncate text-xs text-gray-400">
                          {employee.fullName}
                        </p>
                      )}

                      <p className="truncate text-sm text-gray-500">
                        {employee.employeeCode} ·{" "}
                        {employee.designation || "No designation"}
                      </p>
                    </div>
                  </div>

                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <SessionTimeline employee={employee} />
                      {hasMultipleSessions ? (
                        <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-800">
                          {sessionCount} sessions
                        </span>
                      ) : null}
                    </div>

                    <p className="mt-2 text-sm text-gray-600">
                      First in <b>{formatTime(employee.checkInAt)}</b> · Final
                      out <b>{formatTime(employee.checkOutAt)}</b> · Worked{" "}
                      <b>{formatMinutes(employee.workingMinutes)}</b>
                    </p>
                  </div>

                  <span
                    className={`inline-flex h-fit items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${classes}`}
                  >
                    <Icon size={14} />
                    {label}
                  </span>
                </div>
              );
            })
          )}
        </div>

        {!loading && employees.length > 0 && (
          <div className="flex flex-col gap-4 border-t p-5 md:flex-row md:items-center md:justify-between">
            <p className="text-sm text-gray-500">
              Showing{" "}
              <span className="font-semibold text-gray-900">{startItem}</span>
              {" – "}
              <span className="font-semibold text-gray-900">{endItem}</span>
              {" of "}
              <span className="font-semibold text-gray-900">
                {employees.length}
              </span>{" "}
              employees
            </p>

            {totalPages > 1 && (
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setCurrentPage((page) => page - 1)}
                  disabled={currentPage === 1}
                  className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:border-red-200 hover:bg-red-50 hover:text-red-800 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Previous
                </button>

                {pageNumbers.map((page) => (
                  <button
                    key={page}
                    type="button"
                    onClick={() => setCurrentPage(page)}
                    className={`h-10 w-10 rounded-xl text-sm font-semibold transition ${
                      page === currentPage
                        ? "bg-red-800 text-white shadow-sm"
                        : "border border-gray-200 bg-white text-gray-700 hover:border-red-200 hover:bg-red-50 hover:text-red-800"
                    }`}
                  >
                    {page}
                  </button>
                ))}

                <button
                  type="button"
                  onClick={() => setCurrentPage((page) => page + 1)}
                  disabled={currentPage === totalPages}
                  className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:border-red-200 hover:bg-red-50 hover:text-red-800 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <ManualAttendanceModal
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        accessToken={accessToken}
        attendanceDate={selectedDate}
        onSaved={loadAttendance}
      />

      <ImportAttendanceModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        accessToken={accessToken}
        onImported={loadAttendance}
      />
    </div>
  );
}
